import React from "react";
import { api } from "./api.jsx";
import { GAMES } from "./data.jsx";
import { useApp } from "./store.jsx";
import { SetReports } from "./admin-set-reports.jsx";
import { AdminCampaigns } from "./admin-campaigns.jsx";
import { Dialog, useConfirm } from "./chrome.jsx";

const { Button, Card, Tag, Icon, EmptyState, Input, Select } = window.CardboardmarketDesignSystem_2a3239;

/**
 * The queue of pictures somebody says are wrong.
 *
 * DELIBERATELY PLAIN, and it is worth saying why rather than letting it look
 * unfinished. This screen is read by about four people who already know what a
 * card code is; every minute spent styling it is a minute not spent on the
 * screens traders actually use. What it does owe them is being unambiguous: the
 * pictures are large enough to tell apart, and the decision is stated in words
 * rather than icons.
 *
 * IN ENGLISH, alone among the screens. It is not a trader-facing surface, the
 * vocabulary is the catalogue's own — `art_key`, card code, printing — and
 * translating an internal tool would mean maintaining Spanish for terms that
 * only ever appear in our own database. Every other screen goes through `t()`.
 *
 * IT IS NOT A ROUTE ANYBODY CAN GUESS INTO. The API answers 404 to a trader who
 * is not an operator, so the worst a curious person finds here is an empty list
 * and an error. The link is hidden as a courtesy, not as the protection.
 */
/**
 * Where the platform is working.
 *
 * TOTALS FIRST, then a day-by-day table, then the same numbers by province. That
 * order is the question the owner actually asked — "how is it doing, and where
 * is it succeeding and where is it not" — rather than the order the data happens
 * to arrive in.
 *
 * IT SAYS HOW OLD IT IS, prominently. The numbers are recomputed on a schedule
 * rather than on load, because each row costs a point-in-polygon per listing and
 * per trade. A dashboard that hides its staleness invites somebody to read
 * yesterday as this morning, and then to make a decision on it.
 *
 * NO TRADER IS EVER SHOWN A PROVINCE. This screen counts them; nothing here can
 * name one. See migration 066 for the line: a province may exist in an
 * aggregate, never on a person.
 */
function Overview() {
  const [state, setState] = React.useState({ loading: true, days: [], refreshedAt: null, error: null });
  const [rebuilding, setRebuilding] = React.useState(false);
  const [problem, setProblem] = React.useState(null);

  const load = React.useCallback(() => api.adminOverview(30)
    .then((r) => setState({ loading: false, days: r.days, refreshedAt: r.refreshedAt, error: null }))
    .catch((e) => setState({ loading: false, days: [], refreshedAt: null, error: e })), []);

  React.useEffect(() => {
    let live = true;
    api.adminOverview(30)
      .then((r) => { if (live) setState({ loading: false, days: r.days, refreshedAt: r.refreshedAt, error: null }); })
      .catch((e) => { if (live) setState({ loading: false, days: [], refreshedAt: null, error: e }); });
    return () => { live = false; };
  }, []);

  /*
   * REBUILT ON DEMAND, then read back rather than patched in.
   *
   * The endpoint returns the new timestamp, and it would be less code to drop
   * that straight into state — but the numbers on screen came from the view, so
   * reading the view again is what makes the screen agree with itself. A page
   * that updated its "recomputed at" line without changing a single figure below
   * it would be the most confusing possible outcome of pressing this.
   */
  const rebuild = async () => {
    setRebuilding(true); setProblem(null);
    try {
      await api.adminRefreshOverview();
      await load();
    } catch (e) {
      setProblem(e && e.code === 'overview_refresh_in_flight'
        ? 'Already rebuilding. Give it a moment.'
        : 'It could not be rebuilt. Try again in a moment.');
    } finally { setRebuilding(false); }
  };

  if (state.loading) return <p style={{ font: 'var(--type-body)' }}>Loading the overview…</p>;
  if (state.error) {
    return <EmptyState icon="alert-triangle" title="The overview would not load"
             description={String(state.error?.message || state.error)} />;
  }
  if (!state.days.length) {
    return <EmptyState icon="bar-chart" title="Nothing to report yet"
             description="No listings or trades in the last 30 days." />;
  }

  const sum = (rows, key) => rows.reduce((n, r) => n + (r[key] || 0), 0);

  // By day, newest first: several provinces can share a day, so they fold.
  const byDay = new Map();
  for (const r of state.days) {
    const at = byDay.get(r.day) || { day: r.day, tradersJoined: 0, listingsPosted: 0, listingsImported: 0, tradesProposed: 0, tradesCompleted: 0, reportsMade: 0 };
    at.tradersJoined += r.tradersJoined;
    at.listingsImported += r.listingsImported;
    at.listingsPosted += r.listingsPosted;
    at.tradesProposed += r.tradesProposed;
    at.tradesCompleted += r.tradesCompleted;
    at.reportsMade += r.reportsMade;
    byDay.set(r.day, at);
  }

  const byProvince = new Map();
  for (const r of state.days) {
    // Null is a real answer — a trader with no point, or one outside Spain — and
    // it is named rather than dropped, because a total that silently excludes
    // rows is a total nobody can reconcile.
    const key = r.province || 'Outside Spain / no point';
    const at = byProvince.get(key) || { province: key, tradersJoined: 0, listingsPosted: 0, listingsImported: 0, tradesProposed: 0, tradesCompleted: 0, reportsMade: 0 };
    at.tradersJoined += r.tradersJoined;
    at.listingsImported += r.listingsImported;
    at.listingsPosted += r.listingsPosted;
    at.tradesProposed += r.tradesProposed;
    at.tradesCompleted += r.tradesCompleted;
    at.reportsMade += r.reportsMade;
    byProvince.set(key, at);
  }

  const cell = { padding: 'var(--space-2) var(--space-3)', textAlign: 'right', font: 'var(--type-body)' };
  const head = { ...cell, font: 'var(--type-meta)', color: 'var(--text-muted)', whiteSpace: 'nowrap' };
  const left = { ...cell, textAlign: 'left' };

  const Table = ({ title, rows, firstColumn, keyOf }) => (
    <Card>
      <h2 style={{ font: 'var(--type-body-strong)', margin: '0 0 var(--space-3)' }}>{title}</h2>
      {/* Wide tables scroll inside their own box rather than the page. */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 460 }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{firstColumn}</th>
              <th style={head}>New traders</th>
              <th style={head}>Listings</th>
              <th style={head}>Imported</th>
              <th style={head}>Proposed</th>
              <th style={head}>Completed</th>
              <th style={head}>Reports</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={keyOf(r)} style={{ borderTop: '1px solid var(--border-hairline)' }}>
                <td style={left}>{keyOf(r)}</td>
                <td style={cell}>{r.tradersJoined}</td>
                <td style={cell}>{r.listingsPosted}</td>
                <td style={cell}>{r.listingsImported}</td>
                <td style={cell}>{r.tradesProposed}</td>
                <td style={cell}>{r.tradesCompleted}</td>
                <td style={cell}>{r.reportsMade}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <header style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ font: 'var(--type-title)', margin: 0 }}>How the platform is doing</h1>
          <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
            Last 30 days.{' '}
            {state.refreshedAt
              ? `Recomputed ${new Date(state.refreshedAt).toLocaleString('en-GB')} — not live.`
              : 'Recomputed on a schedule, not live.'}
          </p>
          {problem ? (
            <p style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--status-danger)', margin: 'var(--space-2) 0 0' }}>
              {problem}
            </p>
          ) : null}
        </div>
        {/*
          Beside the line that says how old it is, because that sentence is what
          makes somebody want the button. The nightly rebuild stays the default;
          this is for the minute after you changed something and want to know
          whether it moved.
        */}
        <Button size="sm" variant="outline" iconLeft="refresh-cw" disabled={rebuilding}
                data-cbm="overview-rebuild" onClick={rebuild}>
          {rebuilding ? 'Rebuilding…' : 'Rebuild now'}
        </Button>
      </header>

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {[
          ['New traders', sum(state.days, 'tradersJoined')],
          ['Listings posted', sum(state.days, 'listingsPosted')],
          ['…of those imported', sum(state.days, 'listingsImported')],
          ['Trades proposed', sum(state.days, 'tradesProposed')],
          ['Trades completed', sum(state.days, 'tradesCompleted')],
          ['Reports', sum(state.days, 'reportsMade')],
        ].map(([label, n]) => (
          <Card key={label} style={{ flex: '1 1 160px' }}>
            <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{label}</div>
            <div style={{ font: 'var(--type-title)' }}>{n}</div>
          </Card>
        ))}
      </div>

      <Table title="By province" firstColumn="Province"
        rows={[...byProvince.values()].sort((a, b) => b.listingsPosted - a.listingsPosted)}
        keyOf={(r) => r.province} />

      <Table title="By day" firstColumn="Day"
        rows={[...byDay.values()].sort((a, b) => (a.day < b.day ? 1 : -1))}
        keyOf={(r) => r.day} />
    </div>
  );
}

/**
 * Supplying a picture the catalogue has none of.
 *
 * WHY IT NEEDS A SCREEN AT ALL, rather than being a thing somebody does with a
 * database client: the whole point of relaxing the all-or-none picture rule was
 * that a blank chip beside a real scan is a VISIBLE gap somebody can close. An
 * endpoint nobody can reach closes nothing — the API shipped without this and
 * the first thing the owner said was "I don't see where I can see the admin
 * upload".
 *
 * BY PRODUCT ID, which is the honest identifier. A product IS a printing, so a
 * picture attached to one is unambiguous by construction — and the id is what
 * the picker shows on a chip it cannot otherwise tell apart, so an operator
 * looking at the problem already has the number in front of them.
 */
function SupplyPicture() {
  /*
   * FIND THE PRINTING, then supply the picture.
   *
   * The first version asked for a product id and nothing else, which the owner
   * called out: "the upload of the backoffice lacks a few things like a proper
   * search bar by code/set/name or whatever and maybe some a game filter." Quite
   * right — an operator looking at a blank chip knows the CARD, not a number, and
   * making them go and find one by hand is how a tool goes unused.
   *
   * It reuses the catalogue's own search and printings endpoints, so the list an
   * operator picks from is the same list a seller sees. Searching by code works
   * because a product's name carries it: "Sabo (P-105)".
   */
  const [q, setQ] = React.useState('');
  const [game, setGame] = React.useState('');
  const [hits, setHits] = React.useState([]);
  const [looking, setLooking] = React.useState(false);
  const [printings, setPrintings] = React.useState(null);
  const [productId, setProductId] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(null);
  const [error, setError] = React.useState(null);
  const fileInput = React.useRef(null);
  const [confirm, confirmDialog] = useConfirm();

  const id = Number(productId.trim());
  const can = Number.isInteger(id) && id > 0 && file && !busy;

  // Deliberately a button rather than a keystroke search: an operator types a
  // whole card name, and this is a catalogue of 311,000 products.
  const search = async () => {
    setLooking(true); setError(null); setPrintings(null);
    try {
      const r = await api.searchSuggestions({ q: q.trim(), ...(game ? { game } : {}), limit: 10 });
      setHits(r.suggestions || []);
    } catch (e) { setError(e); } finally { setLooking(false); }
  };

  const openCard = async (hit, fresh) => {
    setLooking(true); setError(null);
    try {
      const r = await api.cardPrintings({ productId: hit.productId }, undefined, fresh);
      setPrintings({ name: hit.name, items: r.printings || [] });
      setHits([]);
    } catch (e) { setError(e); } finally { setLooking(false); }
  };

  const send = async () => {
    setBusy(true); setError(null); setDone(null);
    try {
      const r = await api.adminUploadCardImage(id, file);
      setDone(r.image);
      setFile(null);
      /*
       * SHOW THEM WHAT THEY JUST DID.
       *
       * The list was left exactly as it was, so a row that had just been fixed
       * went on saying "not shown on listings" until the whole card was searched
       * again — and even then the browser served its five-minute copy of the
       * answer, taken before the upload. An operator reasonably concluded the
       * upload had not worked, on production, with the server already right.
       *
       * `fresh` is what makes the re-read mean anything; without it this is the
       * same stale body arriving twice.
       */
      if (printings) await openCard({ productId: Number(id) }, true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 520 }}>
      {confirmDialog}
      <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>
        For a printing the catalogue has no picture of. It beats the catalogue and
        the mirror, and still loses to a seller&rsquo;s own photograph.
      </p>

      {/*
        NO HINTS INSIDE THE ROW. `align-items: flex-end` lines controls up by
        their BOTTOM edge, and a hint under one of them is part of that edge — so
        the field with the hint sat two lines higher than the select and the
        button beside it. The hint moves below the whole row, where it describes
        the row anyway.
      */}
      <div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Input
            wrapperStyle={{ flex: '1 1 200px' }}
            label="Find the card"
            icon="search"
            placeholder="Sabo (P-105)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }}
          />
          <Select
            wrapperStyle={{ flex: '0 1 150px' }}
            label="Game"
            value={game}
            onChange={setGame}
            options={[{ value: '', label: 'All games' }].concat(
              GAMES.map((g) => ({ value: g.id, label: g.label })),
            )}
          />
          <Button variant="outline" onClick={search} disabled={looking || q.trim().length < 2}>
            {looking ? 'Looking…' : 'Search'}
          </Button>
        </div>
        <span style={{ display: 'block', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)',
                       color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
          Name or code — a product&rsquo;s name carries its code.
        </span>
      </div>

      {hits.length ? (
        <Card padding="0" style={{ overflow: 'hidden' }}>
          {hits.map((h, i) => (
            <button key={h.productId} type="button" onClick={() => openCard(h)}
              style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', width: '100%',
                       padding: '10px 12px', background: 'none', border: 0, cursor: 'pointer',
                       borderTop: i ? '1px solid var(--border-hairline)' : 'none', textAlign: 'left' }}>
              <span style={{ flex: 1, font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
                {h.name}
              </span>
              {/* The code, not the game: it is what an operator is matching
                  against, and a suggestion carries no game field. */}
              <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{h.cardCode || ''}</span>
            </button>
          ))}
        </Card>
      ) : null}

      {printings ? (
        <Card padding="0" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', font: 'var(--type-meta)', color: 'var(--text-muted)',
                        borderBottom: '1px solid var(--border-hairline)' }}>
            {printings.name} · pick the printing to supply
          </div>
          {/*
            ONE ROW PER PRODUCT HERE, not per set.
            `/api/catalog/printings` answers one entry per SET, with the other
            copies nested inside as `versions`. That is right for the screen it
            was built for — a seller picking which printing they hold chooses a
            set, because where the pictures cannot tell two copies apart the set
            IS the thing being chosen.

            It is wrong here, and the owner caught it: an override is stored per
            `source_product_id`, so supplying a picture for one copy does nothing
            for its sibling — and the sibling had no row, so it could not be
            picked at all. Two of this card's six products were unreachable from
            the one screen that exists to fix them. The id field accepts typing,
            which is a workaround for somebody who already knows the number of a
            product the screen is hiding.

            So the rows are flattened back out. The set name repeats down the
            column, which is the correct trade: a repeated heading is noise, an
            unfixable product is a dead end.
          */}
          {printings.items.flatMap((p) => (
            (p.versions && p.versions.length > 1)
              ? p.versions.map((v) => ({
                  productId: v.productId,
                  expansion: p.expansion,
                  // The copy's own name where the catalogue has one, the V.1/V.2
                  // counting where it does not — the same label the seller sees.
                  printingName: v.label || p.printingName,
                  rarity: p.rarity,
                  // Its own picture if this copy has one; otherwise the set's,
                  // which carries the set's answer about whether it is a guess.
                  imageUrl: v.imageUrl || p.imageUrl,
                  pictureIsAGuess: v.imageUrl ? false : p.pictureIsAGuess,
                }))
              : [p]
          )).map((p, i) => (
            <button key={p.productId} type="button"
              onClick={() => { setProductId(String(p.productId)); setDone(null); }}
              style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', width: '100%',
                       padding: 'var(--space-3) var(--space-4)', border: 0, cursor: 'pointer', textAlign: 'left',
                       borderTop: i ? '1px solid var(--border-hairline)' : 'none',
                       background: String(p.productId) === productId ? 'var(--surface-tint)' : 'none' }}>
              {/*
                BIG ENOUGH TO TELL TWO PRINTINGS APART, which is the entire job of
                this list. At 30px they were identifiable as "a card" and nothing
                more — and an operator here is looking at two rows that share a
                set name and differ only in the artwork.
              */}
              {/*
                A GUESSED PICTURE IS DRAWN AS ONE, because this row is the reason
                the screen exists and it used to look identical to a solved one.
                Reported: a listing showed a blank frame, the operator came here
                to supply a picture, saw a thumbnail on every row — including the
                very printing that was blank — and concluded nothing was wrong.

                The thumbnail is real. It is the picture for the card CODE, which
                the catalogue keeps roughly one of, so every printing of the card
                shows the same file. A listing refuses to show it where the
                publisher drew that code more than once, because it would be a
                coin flip between drawings and a confidently wrong card is worse
                than no card. So the row below has a picture AND shows none.
              */}
              {p.imageUrl
                ? <img src={p.imageUrl} alt="" loading="lazy"
                    title={p.pictureIsAGuess
                      ? 'The card-code picture, shared by every printing of this card. Listings do not show it.'
                      : undefined}
                    style={{ width: 72, height: 100, objectFit: 'contain', borderRadius: 'var(--radius-xs)',
                             background: 'var(--kraft-100)',
                             // Faded, so the eye does not read it as this printing's own.
                             opacity: p.pictureIsAGuess ? 0.45 : 1 }} />
                : <span style={{ width: 72, height: 100, borderRadius: 'var(--radius-xs)', background: 'var(--kraft-200)',
                                 display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                 font: 'var(--type-meta)', color: 'var(--text-faint)' }}>none</span>}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
                  {p.expansion || 'Set not named'}
                </span>
                <span style={{ display: 'block', font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                  {[p.printingName, p.rarity, `#${p.productId}`].filter(Boolean).join(' · ')}
                </span>
              </span>
              {/*
                Both of these mean "this printing needs you", and they are said
                differently because they are different situations: one has
                nothing at all, the other has something a listing will not use.
              */}
              {!p.imageUrl
                ? <Tag tone="caution">no picture</Tag>
                : p.pictureIsAGuess
                  ? <Tag tone="caution">not shown on listings</Tag>
                  : null}
              {/*
                FULL SIZE IN A NEW TAB, and separate from choosing. A row that
                both selects and opens would do one of them by accident; the tab
                also means comparing two printings does not lose the list.
              */}
              {p.imageUrl ? (
                <a href={p.imageUrl} target="_blank" rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()}
                   style={{ font: 'var(--type-meta)', color: 'var(--text-link)', whiteSpace: 'nowrap' }}>
                  Full size
                </a>
              ) : null}
            </button>
          ))}
        </Card>
      ) : null}

      <Input
        label="Product id"
        inputMode="numeric"
        placeholder="858537"
        value={productId}
        onChange={(e) => setProductId(e.target.value.replace(/[^0-9]/g, ''))}
        hint="Filled in by picking above, or typed if you already know it."
      />

      {/*
        A BUTTON, NOT THE BROWSER'S OWN FILE CONTROL. `<input type="file">` draws
        itself in the browser's chrome and its own locale — "Seleccionar archivo
        / Ningún archivo seleccionado" in the middle of an English screen — which
        is neither our type, our spacing nor our language. The input still does
        the work; it is simply not the thing on screen.
      */}
      <div>
        <span className="cbm-label" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>Picture</span>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setDone(null); }}
          style={{ display: 'none' }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outline" size="sm" iconLeft="image" onClick={() => fileInput.current?.click()}>
            {file ? 'Choose another' : 'Choose a picture'}
          </Button>
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: file ? 'var(--text-strong)' : 'var(--text-muted)' }}>
            {file ? file.name : 'Nothing chosen yet'}
          </span>
        </div>
        <span style={{ display: 'block', font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)',
                       color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
          Resized, re-encoded and given its smaller copies here — send the best one you have.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
        <Button onClick={send} disabled={!can}>{busy ? 'Uploading…' : 'Supply the picture'}</Button>
        {done ? (
          <Button variant="ghost" size="sm" onClick={async () => {
            if (!(await confirm({
              title: "Take this picture back off?",
              description: "It is deleted from this printing. Uploading it again is the only way back.",
              confirmLabel: "Take it off",
            }))) return;
            setBusy(true);
            try { await api.adminRemoveCardImage(done.sourceProductId); setDone(null); }
            catch (e) { setError(e); } finally { setBusy(false); }
          }}>Take it back off</Button>
        ) : null}
      </div>

      {error ? (
        <EmptyState icon="alert-triangle" title="That did not go up"
          description={String(error?.message || error)} />
      ) : null}

      {done ? (
        <Card style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
          {/* Shown, not merely reported. An upload that says "done" and cannot be
              looked at is one nobody trusts — and the first version of this
              endpoint stored a zero-byte file and reported success. */}
          <img src={done.url} alt="" style={{ width: 96, borderRadius: 'var(--radius-sm)', background: 'var(--kraft-100)' }} />
          <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
            <div style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
              {done.name}
            </div>
            <div>{done.cardCode || 'no card code'} · product {done.sourceProductId}</div>
            <div>{done.widths?.length ? `smaller copies: ${done.widths.join(', ')}` : 'no smaller copies'}</div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Shops waiting to be verified.
 *
 * THE FEATURE WAS ENTIRELY DARK. `shop` has an apply-and-verify flow, a
 * rejection note somebody can answer, a nearby lookup and a public view — all
 * on the server, none of it reachable. No screen applied and no screen
 * approved, so production held zero shops and would have gone on holding zero
 * however many stores said yes.
 *
 * A verified shop is a place a trade can be arranged at. For a game store that
 * is the whole offer: people already trading a few streets away agree to meet
 * at your counter.
 *
 * REJECTING TAKES A NOTE, and the server requires one. The commonest rejection
 * is a wrong address, and "no" with no reason is a dead end — the same PUT that
 * applied answers a rejection, so a shop can fix it and come back.
 */
/**
 * WHERE THE TRAFFIC CAME FROM.
 *
 * Asked for as "which people came from an Instagram ad and which from a Facebook
 * ad". Half the answer already existed — `utm_campaign` has been captured and
 * reported as a funnel for a while — and the other half did not: only the
 * campaign was read, and one campaign runs on both placements at once.
 *
 * THE ENDPOINT ALSO HAD NO SCREEN. `/api/events/summary` has been reachable by
 * curl and by nothing else, which is why nobody has been reading it. A number
 * that takes a terminal to see is a number nobody looks at, and "which ad
 * worked" is exactly the question somebody asks on a phone the morning after
 * spending money.
 *
 * THE FUNNEL IS THE POINT, not the visitor count. Visitors say an ad was
 * clicked; signups say it was persuasive; a posted listing says the person had
 * cards; a proposed trade says the product solved what the ad promised. A
 * placement with visitors and nothing after them is an audience that does not
 * want this, which is worth knowing before spending more on it.
 */
const TRAFFIC_DAYS = [7, 30, 90];

function Traffic() {
  /* The window is in the link as `days`, beside `view=traffic`; anything else is 30. */
  const { route, navigate } = useApp();
  const days = TRAFFIC_DAYS.includes(Number(route.days)) ? Number(route.days) : 30;
  const setDays = (n) => navigate('admin', { view: 'traffic', ...(n === 30 ? {} : { days: String(n) }) }, { replace: true, keepScroll: true });
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    api.eventsSummary(days)
      .then((d) => { if (live) setState({ loading: false, data: d, error: null }); })
      .catch((e) => { if (live) setState({ loading: false, data: null, error: e }); });
    return () => { live = false; };
  }, [days]);

  // The same cell shapes the overview uses, for the same reason it defines them:
  // these tables are numbers in columns and nothing else.
  const cell = { padding: 'var(--space-2) var(--space-3)', textAlign: 'right', font: 'var(--type-body)' };
  const head = { ...cell, font: 'var(--type-meta)', color: 'var(--text-muted)', whiteSpace: 'nowrap' };
  const left = { ...cell, textAlign: 'left' };

  if (state.loading) return <p style={{ font: 'var(--type-body)' }}>Loading…</p>;
  if (state.error) {
    return <EmptyState icon="alert-triangle" title="That would not load"
             description={String(state.error?.message || state.error)} />;
  }

  const d = state.data;

  const Funnel = ({ title, rows, withCampaign, empty }) => (
    <Card>
      <h2 style={{ font: 'var(--type-body-strong)', margin: '0 0 var(--space-3)' }}>{title}</h2>
      {rows.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 460 }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>{withCampaign ? 'Source' : 'Campaign'}</th>
                {withCampaign ? <th style={{ ...head, textAlign: 'left' }}>Campaign</th> : null}
                <th style={head}>Visitors</th>
                <th style={head}>Signups</th>
                <th style={head}>Listings</th>
                <th style={head}>Trades</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.source || ''}-${r.campaign || ''}-${i}`}>
                  <td style={left}>{withCampaign ? r.source : r.campaign}</td>
                  {withCampaign ? <td style={{ ...left, color: 'var(--text-muted)' }}>{r.campaign || '—'}</td> : null}
                  <td style={cell}>{r.visitors}</td>
                  <td style={cell}>{r.signups}</td>
                  <td style={cell}>{r.listingsPosted}</td>
                  <td style={cell}>{r.tradesProposed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Said plainly rather than shown as an empty table: nothing here almost
           always means no link carried the parameter, not that nobody came. */
        <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: 0 }}>{empty}</p>
      )}
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <header style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ font: 'var(--type-title)', margin: 0 }}>Where they come from</h1>
          <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>{d.note}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {[7, 30, 90].map((n) => (
            <Button key={n} size="sm" variant={days === n ? 'secondary' : 'ghost'} onClick={() => setDays(n)}>
              {n} days
            </Button>
          ))}
        </div>
      </header>

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {[['Views', d.totals.views], ['Visitors', d.totals.visitors]].map(([label, n]) => (
          <Card key={label}>
            <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{label}</div>
            <div style={{ font: 'var(--type-h2)' }}>{n}</div>
          </Card>
        ))}
      </div>

      {/* Source first: it is the question this screen was built to answer. */}
      <Funnel title="By source" rows={d.sources || []} withCampaign
              empty="Nothing tagged yet. A visit appears here only when its link carried utm_source — for example ?utm_source=instagram." />
      <Funnel title="By campaign" rows={d.campaigns || []}
              empty="Nothing tagged yet. A visit appears here only when its link carried a utm_campaign we are running." />

      <Card>
        <h2 style={{ font: 'var(--type-body-strong)', margin: '0 0 var(--space-3)' }}>Referrers</h2>
        {d.referrers.length ? (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={{ ...head, textAlign: 'left' }}>Host</th><th style={head}>Visitors</th></tr></thead>
            <tbody>
              {d.referrers.map((r) => (
                <tr key={r.host}><td style={left}>{r.host}</td><td style={cell}>{r.visitors}</td></tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* Worth saying, because an empty referrer list is the NORMAL result for
             paid social: an in-app browser usually sends none, which is the whole
             reason a tagged link is the answer rather than this table. */
          <p style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', margin: 0 }}>
            None recorded. Apps usually strip the referrer — which is why an ad's link has to carry its own tag.
          </p>
        )}
      </Card>
    </div>
  );
}

function ShopQueue() {
  const [shops, setShops] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  /* One note for the one rejection in progress, rather than a map keyed by
     trader — the field lives in a dialog now, so only ever one exists. */
  const [note, setNote] = React.useState('');
  const [rejecting, setRejecting] = React.useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = React.useCallback(async () => {
    setError(null);
    try { setShops((await api.adminShops()).shops || []); } catch (e) { setError(e); }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  const decide = async (traderId, decision) => {
    setBusy(traderId); setError(null);
    try {
      await api.adminDecideShop({
        traderId, decision,
        ...(decision === 'reject' ? { note: note.trim() } : {}),
      });
      await load();
    } catch (e) { setError(e); } finally { setBusy(null); }
  };

  if (!shops && !error) {
    return <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)' }}>Loading.</p>;
  }
  if (error) {
    return <p style={{ font: 'var(--type-body)', color: 'var(--status-danger)' }}>{error.message}</p>;
  }
  if (!shops.length) {
    return (
      <EmptyState icon="store" title="No shops waiting"
        description="A store applies from its own profile. A verified shop becomes a place a trade can be arranged at." />
    );
  }

  /*
   * TAKEN FROM `ui_kits/app/ShopQueueScreen.jsx` in Claude Design, which had
   * this designed before I built it.
   *
   * Three things it does that my first version did not, each with a reason
   * written beside it there:
   *
   *   The AGE of each application, amber past five days — "a queue with no ages
   *   gets worked newest-first by accident."
   *
   *   The reason asked in a DIALOG when you reject, rather than a text field
   *   sitting on every card. The field on every row made the queue look like a
   *   form to fill in; the reason is only ever wanted at the moment of refusing.
   *
   *   Verify as the one solid action. Rejecting is ordinary and reversible;
   *   verifying puts a badge on somebody and moves where their cards are
   *   measured from, which is the consequential half.
   */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: 640 }}>
      {confirmDialog}
      {shops.map((s) => {
        const days = Math.floor((Date.now() - new Date(s.appliedAt).getTime()) / 86_400_000);
        return (
          <Card key={s.traderId} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-strong)' }}>
                  {s.name}
                </div>
                {/* The address is the thing being checked, so it is the second
                    line and not tucked under a disclosure. */}
                <div style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', marginTop: 2 }}>
                  {s.address}
                </div>
              </div>
              <span style={{ font: 'var(--type-meta)', whiteSpace: 'nowrap',
                             color: days >= 5 ? 'var(--amber-700)' : 'var(--text-muted)' }}>
                {days}d
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Icon name="user" size={13} />
                {[s.trader?.displayName, s.trader?.handle ? '@' + s.trader.handle : null].filter(Boolean).join(' ')}
              </span>
              {/* The operator needs to be able to reach the applicant to check
                  them. This is the one place an email travels, and it travels
                  to an operator. */}
              {s.trader?.email ? <span>{s.trader.email}</span> : null}
              {s.phone ? <a href={`tel:${s.phone.replace(/\s+/g, '')}`} style={{ color: 'var(--text-link)' }}>{s.phone}</a> : null}
              {s.website ? (
                <a href={s.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)' }}>
                  {s.website.replace(/^https?:\/\//, '')}
                </a>
              ) : null}
              {s.point ? (
                <a href={`https://www.google.com/maps/search/?api=1&query=${s.point.lat},${s.point.lng}`}
                   target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)' }}>
                  Open in maps
                </a>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              {/* Asked first: a badge on somebody, their page made public, and
                  only another operator's look takes it back (the owner,
                  2026-10-23, "confirmation should be added anywhere we're
                  changing something kind of important"). Reject asks already:
                  its dialog is where the reason is written. */}
              <Button size="sm" disabled={busy === s.traderId}
                      onClick={async () => {
                        if (await confirm({
                          title: `Verify ${s.name}?`,
                          description: "It shows as a verified store, its page goes public and, if it allows it, its counter can be offered as a place to meet.",
                          confirmLabel: "Verify the store",
                        })) void decide(s.traderId, 'verify');
                      }}>Verify</Button>
              <Button size="sm" variant="outline" disabled={busy === s.traderId}
                      onClick={() => { setRejecting(s); setNote(''); }}>Reject</Button>
            </div>
          </Card>
        );
      })}

      <Dialog open={Boolean(rejecting)} width={360}
        title="Why can this not be confirmed?"
        description={rejecting ? rejecting.name : ''}
        closeLabel="Close"
        onClose={() => setRejecting(null)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRejecting(null)}>Cancel</Button>
            {/* Disabled without a reason, deliberately: this text is the whole
                of what the applicant will be told. */}
            <Button size="sm" variant="outline" disabled={!note.trim() || busy === rejecting?.traderId}
                    onClick={() => { const id = rejecting.traderId; setRejecting(null); void decide(id, 'reject'); }}>
              Send the refusal
            </Button>
          </>
        }>
        <Input label="What they should fix" value={note}
               onChange={(e) => setNote(e.target.value)}
               placeholder="We could not find a store at that address. Check the street number."
               hint="They see this word for word, and can send it again." />
      </Dialog>
    </div>
  );
}

const ADMIN_VIEWS = ['reports', 'overview', 'supply', 'sets', 'shops', 'campaigns', 'traffic'];

export function AdminScreen() {
  const { trader, route, navigate } = useApp();
  /*
   * THE SECTION IS IN THE LINK as `view` (owner, 2026-10-22: every tab in the
   * URL, so a reload keeps it), pushed as every tab is (owner, 2026-10-22:
   * "let's unify navigation behaviors"), so Back steps to the section you were
   * on. Not `tab`, which would remount this screen and re-read the reports
   * queue on every switch. Unknown is the reports queue.
   */
  const view = ADMIN_VIEWS.includes(route.view) ? route.view : 'reports';
  const setView = (next) => navigate('admin', next === 'reports' ? {} : { view: next }, { keepScroll: true });
  const [state, setState] = React.useState({ loading: true, cards: [], error: null });
  const [busy, setBusy] = React.useState(null);

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { cards } = await api.adminImageReports();
      setState({ loading: false, cards, error: null });
    } catch (e) {
      setState({ loading: false, cards: [], error: e });
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const [confirm, confirmDialog] = useConfirm();

  const decide = async (card, decision) => {
    /*
     * Asked first (the owner, 2026-10-23: "confirmation should be added
     * anywhere we're changing something kind of important"). Either answer
     * closes every open report on the card for the people who sent them, and
     * "hide" blanks the picture on every listing of it.
     */
    const named = card.cardCode || (card.printing ? card.printing.name : `#${card.sourceProductId}`);
    const reports = `${card.reports} report${card.reports === 1 ? '' : 's'}`;
    if (!(await confirm(decision === 'hide'
      ? {
        title: `Show no picture for ${named}?`,
        description: `Every listing of this card shows a blank frame instead, and the ${reports} on it close.`,
        confirmLabel: 'Show no picture',
      }
      : {
        title: `Keep the picture for ${named}?`,
        description: `The ${reports} on it close, and the picture stays as it is on every listing.`,
        confirmLabel: 'Keep the picture',
      }))) return;
    setBusy(card.cardCode || `#${card.sourceProductId}`);
    try {
      await api.adminDecideImage({
        game: card.game, cardCode: card.cardCode || undefined,
        sourceProductId: card.cardCode ? undefined : card.sourceProductId, decision,
      });
      // Reloaded rather than patched in place: a decision closes every report on
      // the card, and recomputing the queue is cheaper to reason about than
      // predicting what the server did with rows this screen never saw.
      await load();
    } catch (e) {
      setState((s) => ({ ...s, error: e }));
    } finally {
      setBusy(null);
    }
  };

  if (!trader?.isAdmin) {
    return (
      <EmptyState
        icon="lock"
        title="Not for you"
        description="This page is for catalogue operators."
      />
    );
  }

  // Operator screens, one route, the section named by `?view=` (see above).
  //
  // The row scrolls sideways rather than wrapping, and its tabs do not shrink —
  // see `.cbm-admin-tabs`. It was five tabs that made the difference: a flex row
  // takes the space for a new one out of the others, so the gaps closed up as the
  // back office grew.
  const tabs = (
    <div className="cbm-admin-tabs">
      <Button variant={view === 'reports' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('reports')}>
        Reported pictures
      </Button>
      <Button variant={view === 'overview' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('overview')}>
        How it is doing
      </Button>
      <Button variant={view === 'supply' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('supply')}>
        Supply a picture
      </Button>
      <Button variant={view === 'sets' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('sets')}>
        Missing sets
      </Button>
      <Button variant={view === 'shops' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('shops')}>
        Shops waiting
      </Button>
      <Button variant={view === 'campaigns' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('campaigns')}>
        Campaigns
      </Button>
      <Button variant={view === 'traffic' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('traffic')}>
        Where they come from
      </Button>
    </div>
  );
  if (view === 'overview') return <>{tabs}<Overview /></>;
  if (view === 'supply') return <>{tabs}<SupplyPicture /></>;
  if (view === 'sets') return <>{tabs}<SetReports /></>;
  if (view === 'shops') return <>{tabs}<ShopQueue /></>;
  if (view === 'campaigns') return <>{tabs}<AdminCampaigns /></>;
  if (view === 'traffic') return <>{tabs}<Traffic /></>;

  /*
   * EVERY branch below keeps the tabs, and that is not tidiness.
   *
   * The first version returned the empty state bare, so an operator whose queue
   * was clear — which is the normal state, and the state this screen is in most
   * days — had no way to reach the overview at all. A navigation control that
   * disappears when there is nothing to navigate away FROM is the worst time to
   * lose it.
   */
  if (state.loading) return <>{tabs}<p style={{ font: 'var(--type-body)' }}>Loading the queue…</p></>;

  if (state.error) {
    return (
      <>{tabs}
        <EmptyState
          icon="alert-triangle"
          title="The queue would not load"
          description={String(state.error?.message || state.error)}
          action={<Button onClick={load}>Try again</Button>}
        />
      </>
    );
  }

  if (!state.cards.length) {
    return (
      <>{tabs}
        <EmptyState
          icon="check"
          title="Nothing reported"
          description="No open reports about catalogue pictures."
        />
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {confirmDialog}
      {tabs}
      <header>
        <h1 style={{ font: 'var(--type-title)', margin: 0 }}>Reported pictures</h1>
        <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
          {state.cards.length} card{state.cards.length === 1 ? '' : 's'} waiting. Most reported first.
        </p>
      </header>

      {state.cards.map((card) => (
        <Card key={`${card.game} ${card.cardCode || card.sourceProductId}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <strong style={{ font: 'var(--type-body-strong)' }}>
                {card.cardCode || (card.printing ? card.printing.name : `#${card.sourceProductId}`)}
              </strong>
              <Tag>{card.game}</Tag>
              <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                {card.reports} report{card.reports === 1 ? '' : 's'} · {card.kinds.join(', ')}
              </span>
              {card.decided ? (
                <Tag>
                  already decided: {card.decided.showPicture ? 'picture kept' : 'no picture'}
                </Tag>
              ) : null}
            </div>

            {card.notes.length ? (
              <ul style={{ margin: 0, paddingLeft: '1.2em', font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                {card.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            ) : null}

            {/*
              What we hold for this card, at a size where two printings can
              actually be told apart. `srcset` so the grid does not pull the
              full-size scan on a queue of two hundred.

              ONE PICTURE, ALWAYS, since the drawings were retired: a report
              names a PRINTING and a printing has exactly one picture. The
              shortlist of Bandai artworks that used to sit here asked the
              operator to pick a drawing, which is a vocabulary the owner
              scrapped — "let's scrap Bandai's artwork and always use
              CardTrader's because they seem to always be more accurate".
            */}
            {card.printing ? (
              <figure style={{ margin: 0, width: 150 }}>
                <img
                  src={card.printing.imageUrl}
                  srcSet={card.printing.imageSrcset || undefined}
                  sizes="150px"
                  alt={card.printing.name}
                  loading="lazy"
                  style={{
                    width: '100%', aspectRatio: '3/4', objectFit: 'contain',
                    background: 'var(--kraft-100)', borderRadius: 'var(--radius-sm)',
                    border: '2px solid var(--danger-600)',
                  }}
                />
                <figcaption style={{ font: 'var(--type-meta)' }}>
                  {card.printing.expansion || '\u00a0'}
                </figcaption>
              </figure>
            ) : (
              <p style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', margin: 0 }}>
                We hold no picture for this card.
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                iconLeft="eye-off"
                disabled={busy === (card.cardCode || `#${card.sourceProductId}`)}
                onClick={() => decide(card, 'hide')}
              >
                None of these — show no picture
              </Button>
              <Button
                variant="ghost"
                disabled={busy === (card.cardCode || `#${card.sourceProductId}`)}
                onClick={() => decide(card, 'keep')}
              >
                The picture is fine
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
